import React from 'react';

import { useTranslation } from 'react-i18next';
import { Button, Checkbox, FormControlLabel, Hidden, TextField, Typography } from '@mui/material';
import { usePatchChangeShipping } from 'services/Data/CartService';
import CustomInput from '@acnb/CustomComponents/CustomInput/CustomInput';
import SkeletonWrapper from 'Components/SkeletonWrapper';
import GridContainer from '@jumbo/components/GridContainer';
import GridItem from '@jumbo/components/GridItem';
import { useRegions } from 'services/Data/RegionService';
import { translate } from '@acnb/CustomComponents/DropdownItem/Translate';
import { getHighlightParts } from 'services/Utilities/NewHighlights';
import CustomAutocomplete from '@acnb/CustomComponents/CustomAutocomplete/CustomAutocomplete';
import TrackingNumberSection from './TrackingNumberSection';
import { CartStatusEnumValue } from 'enums/CartStatusEnum';

const DeliveryCheckoutSection = ({ cart, isLoading, validator, isDisabled, actionBtns }) => {
  const { t } = useTranslation();

  const { mutate: changeShipping, isPending: changingShipping } = usePatchChangeShipping();
  const { data: regions, isLoading: gettingRegions } = useRegions();

  return (
    <SkeletonWrapper
      isLoading={isLoading || changingShipping || gettingRegions}
      component={
        <GridContainer>
          <GridItem xs={12}>
            <FormControlLabel
              control={
                <Checkbox
                  onChange={({ target: { checked } }) => changeShipping({ ...cart, isDealerShippingAddress: checked })}
                  checked={cart?.isDealerShippingAddress || false}
                  disabled={!cart || isDisabled || cart.isDemo}
                />
              }
              label={t('isDealerShippingAddress')}
            />
          </GridItem>
          <GridItem xs={12}>
            <CustomInput
              labelText={t('shippingAddress')}
              id="shippingAddress"
              formControlProps={{
                fullWidth: true,
              }}
              error={validator.current.message(t('shippingAddress'), cart?.shippingAddress, `required`)}
              inputProps={{
                onBlur: ({ target: { value } }) => {
                  changeShipping({ ...cart, shippingAddress: value });
                },
                disabled: !cart || isDisabled || cart.isDealerShippingAddress,
                defaultValue: cart?.shippingAddress ?? '',
              }}
            />
          </GridItem>

          <GridItem xs={12}>
            <CustomInput
              labelText={t('shippingCity')}
              id="shippingCity"
              formControlProps={{
                fullWidth: true,
              }}
              error={validator.current.message(t('shippingCity'), cart?.shippingCity, `required`)}
              inputProps={{
                onBlur: ({ target: { value } }) => {
                  changeShipping({ ...cart, shippingCity: value });
                },
                disabled: !cart || isDisabled || cart.isDealerShippingAddress,
                defaultValue: cart?.shippingCity ?? '',
              }}
            />
          </GridItem>
          <GridItem xs={12}>
            <CustomAutocomplete
              labelText={t('regions')}
              options={regions || []}
              formControlProps={{
                fullWidth: true,
              }}
              disabled={!cart || isDisabled || cart.isDealerShippingAddress}
              error={validator.current.message(t('region'), cart?.shippingRegion_Id, `required`)}
              autocompleteProps={{
                onChange: (event, value) => {
                  changeShipping({ ...cart, shippingRegion_Id: value.id });
                },
                disableClearable: true,
                isOptionEqualToValue: (option, value) => option?.id === value,
                value: cart?.shippingRegion_Id || null,
                getOptionLabel: option => {
                  let item = regions?.find(se => se?.id === option) || option;

                  return (
                    (item &&
                      `${translate({
                        label: item?.label,
                        t,
                      })}`) ||
                    ''
                  );
                },
                filterOptions: (options, state) => {
                  var searchTerms = state.inputValue.toLowerCase().split(' ');
                  var filteredOptions = options.filter(option =>
                    searchTerms.every(searchTerm =>
                      translate({ label: option.label, t })
                        ?.toLowerCase()
                        .includes(searchTerm),
                    ),
                  );

                  return filteredOptions;
                },
                renderOption: (props, option, { inputValue }) => {
                  const result = getHighlightParts(`${translate({ label: option.label, t })}`, inputValue);

                  return (
                    <div {...props} key={option.id}>
                      {result}
                    </div>
                  );
                },
                renderInput: params => (
                  <TextField
                    {...params}
                    label={t('regions')}
                    variant="outlined"
                    error={!!validator.current.message(t('region'), cart?.shippingRegion_Id, 'required')}
                    sx={{
                      '& .MuiOutlinedInput-root': {
                        borderRadius: 0,
                      },
                    }}
                  />
                ),
              }}
            />
          </GridItem>

          <GridItem xs={12}>
            <CustomInput
              labelText={t('shippingPostalCode')}
              id="shippingPostalCode"
              formControlProps={{
                fullWidth: true,
              }}
              error={validator.current.message(t('shippingPostalCode'), cart?.shippingPostalCode, `required`)}
              inputProps={{
                onBlur: ({ target: { value } }) => {
                  changeShipping({ ...cart, shippingPostalCode: value });
                },
                disabled: !cart || isDisabled || cart.isDealerShippingAddress,
                defaultValue: cart?.shippingPostalCode ?? '',
              }}
            />
          </GridItem>

          {cart?.status > CartStatusEnumValue.Active && (
            <GridItem xs={12}>
              <TrackingNumberSection cart={cart} isCartLoading={isLoading} />
            </GridItem>
          )}

          <GridItem xs={12}>
            {actionBtns
              .filter(actionBtn => !actionBtn.hidden)
              .map(actionBtn => (
                <Hidden mdDown key={actionBtn.label}>
                  <Button
                    sx={{
                      borderRadius: 0,
                      color: '#000',
                      mt: 4,
                      width: '100%',
                      boxShadow: 'none',
                      '&:hover': {
                        backgroundColor: '#EFBA29',
                        boxShadow: 'none',
                      },
                    }}
                    variant={actionBtn.variant}
                    color={actionBtn.color}
                    disabled={!cart || actionBtn.disabled || cart.isDemo}
                    onClick={actionBtn.action}>
                    <Typography variant="h1" sx={{ fontSize: { xs: 16, sm: 18, md: 22 }, py: 2 }}>
                      {actionBtn.label}
                    </Typography>
                  </Button>
                </Hidden>
              ))}
          </GridItem>
        </GridContainer>
      }
    />
  );
};

export default DeliveryCheckoutSection;
